// =============================================================================
// SystemHealthPage — Dashboard de diagnóstico del sistema (ADMIN)
//
// Ruta: /admin/sistema
// Contenido: usuarios, centros, catálogo, cifrado, actividad reciente.
// Reutiliza KpiCard y EmptyBlock del dashboard operativo.
//
// Principios:
//   - Estilo coherente con Sidebar y DashboardPage (Material Symbols + slate/primary)
//   - Sin spinners; placeholders animate-pulse
//   - Degradación elegante: EmptyBlock cuando una sección viene null
//   - Sin polling: botón de refrescar manual
//   - Drill-down en cards con pantalla destino existente (sin inventar filtros)
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getSystemHealth } from '../../api/dashboard';
import type { SystemHealth } from '../../types/system-health';

import { KpiCard } from '../../components/dashboard/KpiCard';
import { EmptyBlock } from '../../components/dashboard/EmptyBlock';

export function SystemHealthPage() {
  const { user } = useAuth();
  const [data, setData] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSystemHealth();
      setData(result);
      setLastRefreshed(new Date());
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al cargar salud del sistema';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatTime = (d: Date): string =>
    d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-6">
      {/* Cabecera */}
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Salud del sistema</h1>
          <p className="text-sm text-slate-500 mt-1">
            Diagnóstico de la empresa{user?.company?.name ? (
              <> · <span className="font-medium text-slate-700">{user.company.name}</span></>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          {lastRefreshed && <span>Actualizado {formatTime(lastRefreshed)}</span>}
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors disabled:opacity-50"
            title="Actualizar"
          >
            <span className="material-symbols-outlined text-lg">refresh</span>
          </button>
        </div>
      </header>

      {/* Error general */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-red-500">error</span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">
              No se pudo cargar la salud del sistema
            </p>
            <p className="text-xs text-red-600 mt-1">{error}</p>
          </div>
          <button
            type="button"
            onClick={fetchData}
            className="text-sm font-medium text-red-700 hover:text-red-900"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* SECCIÓN: USUARIOS */}
      <SectionHeader icon="group" title="Usuarios" />
      {loading || data?.users ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiCard
            icon="person"
            label="Usuarios activos"
            value={data?.users ? `${data.users.active} / ${data.users.total}` : 0}
            tone="primary"
            to="/admin/usuarios"
            loading={loading}
          />
          <KpiCard
            icon="login"
            label="Logins últimos 7 días"
            value={data?.users?.last7dLogins ?? 0}
            tone="slate"
            loading={loading}
          />
          <KpiCard
            icon="person_off"
            label="Usuarios inactivos"
            value={data?.users ? data.users.total - data.users.active : 0}
            tone={
              data?.users && data.users.total - data.users.active > 0 ? 'amber' : 'slate'
            }
            to="/admin/usuarios"
            loading={loading}
          />
        </div>
      ) : (
        <EmptyBlock title="Usuarios" />
      )}

      {/* SECCIÓN: CENTROS */}
      <SectionHeader icon="apartment" title="Centros" />
      {loading || data?.sites ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiCard
            icon="store"
            label="Centros configurados"
            value={data?.sites?.configured ?? 0}
            tone="primary"
            to="/admin/sedes"
            loading={loading}
          />
          <KpiCard
            icon="chat"
            label="Centros con WhatsApp"
            value={
              data?.sites
                ? `${data.sites.withWhatsapp} / ${data.sites.configured}`
                : 0
            }
            tone="slate"
            loading={loading}
          />
          <KpiCard
            icon="wifi"
            label="WhatsApp conectado"
            value={
              data?.sites
                ? `${data.sites.whatsappConnected} / ${data.sites.withWhatsapp}`
                : 0
            }
            subLabel="Sesiones activas"
            tone={
              data?.sites &&
              data.sites.withWhatsapp > 0 &&
              data.sites.whatsappConnected < data.sites.withWhatsapp
                ? 'amber'
                : 'green'
            }
            to="/admin/whatsapp"
            loading={loading}
          />
        </div>
      ) : (
        <EmptyBlock title="Centros" />
      )}

      {/* SECCIÓN: CATÁLOGO */}
      <SectionHeader icon="inventory_2" title="Catálogo" />
      {loading || data?.catalog ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon="health_and_safety"
            label="Aseguradoras"
            value={data?.catalog?.insuranceCompanies ?? 0}
            tone="primary"
            to="/admin/aseguradoras"
            loading={loading}
          />
          <KpiCard
            icon="policy"
            label="Pólizas activas"
            value={data?.catalog?.activePolicies ?? 0}
            tone="primary"
            to="/admin/polizas"
            loading={loading}
          />
          <KpiCard
            icon="receipt_long"
            label="Tarifas activas"
            value={data?.catalog?.activeTariffs ?? 0}
            tone="primary"
            to="/admin/tarifas"
            loading={loading}
          />
          <KpiCard
            icon="event_busy"
            label="Vencen en 30 días"
            value={data?.catalog?.tariffsExpiringSoon ?? 0}
            subLabel="Tarifas próximas a expirar"
            tone={
              (data?.catalog?.tariffsExpiringSoon ?? 0) > 0 ? 'amber' : 'slate'
            }
            to="/admin/tarifas"
            loading={loading}
          />
        </div>
      ) : (
        <EmptyBlock title="Catálogo" />
      )}

      {/* SECCIÓN: CIFRADO */}
      <SectionHeader icon="lock" title="Cifrado de datos" />
      {loading || data?.encryption ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiCard
            icon={data?.encryption?.status === 'OK' ? 'verified' : 'error'}
            label="Estado de cifrado"
            value={data?.encryption?.status ?? '—'}
            subLabel="AES-256-GCM por paciente"
            tone={data?.encryption?.status === 'OK' ? 'green' : 'red'}
            loading={loading}
          />
          <KpiCard
            icon="group"
            label="Pacientes con clave"
            value={data?.encryption?.patientsWithEncryptionKey ?? 0}
            tone="slate"
            loading={loading}
          />
          <KpiCard
            icon="groups"
            label="Pacientes totales"
            value={data?.encryption?.patientsTotal ?? 0}
            tone="slate"
            loading={loading}
          />
        </div>
      ) : (
        <EmptyBlock title="Cifrado de datos" />
      )}

      {data?.encryption?.status === 'KO' && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-red-500">warning</span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">
              Hay pacientes sin clave de cifrado
            </p>
            <p className="text-xs text-red-600 mt-1">
              {data.encryption.patientsTotal - data.encryption.patientsWithEncryptionKey} paciente(s) sin DEK asociado.
              Revisar en el próximo mantenimiento.
            </p>
          </div>
        </div>
      )}

      {/* SECCIÓN: ACTIVIDAD RECIENTE */}
      <SectionHeader icon="schedule" title="Actividad últimas 24h" />
      {loading || data?.recentActivity ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KpiCard
            icon="description"
            label="Presupuestos creados"
            value={data?.recentActivity?.budgetsLast24h ?? 0}
            tone="primary"
            loading={loading}
          />
          <KpiCard
            icon="payments"
            label="Pagos recibidos"
            value={data?.recentActivity?.paymentsLast24h ?? 0}
            tone="green"
            loading={loading}
          />
          <KpiCard
            icon="person_add"
            label="Pacientes nuevos"
            value={data?.recentActivity?.patientsCreatedLast24h ?? 0}
            tone="primary"
            loading={loading}
          />
        </div>
      ) : (
        <EmptyBlock title="Actividad últimas 24h" />
      )}

      {/* Footer */}
      {data && !loading && (
        <footer className="text-xs text-slate-400 text-center pt-4">
          Generado: {new Date(data.generatedAt).toLocaleString('es-ES')}
        </footer>
      )}
    </div>
  );
}

interface SectionHeaderProps {
  icon: string;
  title: string;
}

function SectionHeader({ icon, title }: SectionHeaderProps) {
  return (
    <header className="flex items-center gap-2">
      <span className="material-symbols-outlined text-primary">{icon}</span>
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
    </header>
  );
}

export default SystemHealthPage;
